import { useState } from 'react'
import type { InvoiceTransaction, TransactionRow } from '../../../../api/useFreight/types'
import type { CustomerOrder } from '../../../../api/useFreight/types/dashboard'

export interface DrawerState {
  visible: boolean
  orderNumber?: string
  orders: TransactionRow[] | InvoiceTransaction[] | CustomerOrder[]
  currentIndex: number
}

export interface DrawerActions {
  triggerDrawer: (state: {
    visible: boolean
    orderNumber?: string
    currentIndex: number
    orders: TransactionRow[] | InvoiceTransaction[] | CustomerOrder[]
  }) => void
  handleNavigate: (newIndex: number) => void
  closeDrawer: () => void
}

/**
 * Hook to manage drawer state and actions for the freight launchpad
 * @returns DrawerState and actions to manipulate it
 */
export const useLaunchpadDrawer = (): [DrawerState, DrawerActions] => {
  const [drawerState, setDrawerState] = useState<DrawerState>({
    visible: false,
    orders: [],
    currentIndex: -1,
  })

  /**
   * Show the drawer with order details
   */
  const triggerDrawer = ({
    visible,
    orderNumber,
    currentIndex,
    orders,
  }: {
    visible: boolean
    orderNumber?: string
    currentIndex: number
    orders: TransactionRow[] | InvoiceTransaction[] | CustomerOrder[]
  }) => {
    setDrawerState({ visible, orderNumber, orders, currentIndex })
  }

  /**
   * Navigate between orders in the drawer
   */
  const handleNavigate = (newIndex: number) => {
    if (drawerState.orders?.[newIndex]) {
      setDrawerState({
        ...drawerState,
        orderNumber: drawerState.orders[newIndex].order_number.toString() || undefined,
        currentIndex: newIndex,
      })
    }
  }

  /**
   * Close the drawer while preserving other state
   */
  const closeDrawer = () => {
    setDrawerState({ ...drawerState, visible: false })
  }

  return [
    drawerState,
    {
      triggerDrawer,
      handleNavigate,
      closeDrawer,
    },
  ]
}
